import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import './HomePage.css';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../config/contact';
import ContactChannels from '../components/ContactChannels';

const capabilities = [
  {
    title: 'Datos y BI',
    copy: 'Convierte información dispersa en tableros, indicadores y decisiones claras.',
    tags: ['Analítica', 'Dashboards', 'Automatización'],
    link: '/servicios/data',
    image: '/images/BI.webp',
  },
  {
    title: 'Software a medida',
    copy: 'Plataformas robustas que conectan procesos, equipos y oportunidades de negocio.',
    tags: ['Web', 'APIs', 'Integraciones'],
    link: '/servicios/ingenieria',
    image: '/images/SW (3).webp',
  },
  {
    title: 'Cloud',
    copy: 'Infraestructura segura y escalable para operar con velocidad y continuidad.',
    tags: ['Arquitectura', 'Migración', 'DevOps'],
    link: '/servicios/cloud',
    image: '/images/cloud2.webp',
  },
  {
    title: 'Experiencias digitales',
    copy: 'Interfaces simples, accesibles y enfocadas en convertir visitas en acciones.',
    tags: ['UX/UI', 'Frontend', 'Producto'],
    link: '/servicios/design',
    image: '/images/sitioweb (2).webp',
  },
  {
    title: 'Innovación aplicada',
    copy: 'Identificamos oportunidades y las convertimos en soluciones viables y medibles.',
    tags: ['Estrategia', 'IA', 'Prototipos'],
    link: '/servicios/innovacion',
    image: '/images/IA.webp',
  },
];

const proofPoints = [
  { value: '6', label: 'capacidades conectadas' },
  { value: '12', label: 'industrias atendidas' },
  { value: 'End-to-end', label: 'estrategia, diseño y construcción' },
  { value: 'Quito + LATAM', label: 'visión local, estándares globales' },
];

const businessOutcomes = [
  {
    number: '01',
    title: 'Más claridad',
    copy: 'La información importante llega a las personas correctas, en el momento correcto.',
    image: '/images/DATA (2).webp',
    alt: 'Equipo revisando gráficos de indicadores en una tableta y documentos impresos',
    practice: [
      'Tableros e indicadores compartidos por área',
      'Modelos de datos que unifican fuentes dispersas',
      'Señales predictivas para anticipar tendencias',
    ],
    service: { name: 'Analítica de Datos', path: '/servicios/data' },
  },
  {
    number: '02',
    title: 'Menos fricción',
    copy: 'Automatizamos tareas repetitivas y conectamos sistemas que hoy trabajan separados.',
    image: '/images/home-carousel-soporte.webp',
    alt: 'Ingeniera configurando un sistema automatizado desde su portátil',
    practice: [
      'Automatización de tareas y flujos repetitivos',
      'APIs e integraciones entre sistemas existentes',
      'Menos reprocesos y carga manual',
    ],
    service: { name: 'Ingeniería de Software', path: '/servicios/ingenieria' },
  },
  {
    number: '03',
    title: 'Mejor experiencia',
    copy: 'Productos digitales fáciles de usar para clientes, equipos y aliados.',
    image: '/images/home-hero-ingenieria.webp',
    alt: 'Profesional de tecnología desarrollando una solución empresarial',
    practice: [
      'Investigación con usuarios y prototipos validados',
      'Interfaces accesibles, rápidas y consistentes',
      'Experiencias web y móviles para clientes y equipos',
    ],
    service: { name: 'Diseño UX/UI', path: '/servicios/design' },
  },
  {
    number: '04',
    title: 'Crecimiento sostenible',
    copy: 'Arquitecturas preparadas para evolucionar sin reconstruir desde cero.',
    image: '/images/home-hero-transformacion.webp',
    alt: 'Especialista revisando infraestructura en un centro de datos',
    practice: [
      'Arquitectura cloud segura, escalable y resiliente',
      'Modernización por etapas, sin detener la operación',
      'Prácticas DevOps para entregar con continuidad',
    ],
    service: { name: 'Soluciones Cloud', path: '/servicios/cloud' },
  },
];

const industries = [
  ['Banca', '/industrias/banca'],
  ['Retail', '/industrias/retail'],
  ['Salud', '/industrias/salud'],
  ['Manufactura', '/industrias/manufactura'],
  ['Energía', '/industrias/energia'],
  ['Automotriz', '/industrias/automotriz'],
  ['Seguros', '/industrias/seguros'],
  ['Tecnología', '/industrias/tecnologia'],
];

const deliverySteps = [
  {
    label: 'Entender',
    title: 'Empezamos por el negocio',
    copy: 'Definimos el problema, los usuarios, las restricciones y el resultado que realmente importa.',
  },
  {
    label: 'Diseñar',
    title: 'Hacemos visible la solución',
    copy: 'Alineamos arquitectura, experiencia y prioridades antes de invertir en desarrollo.',
  },
  {
    label: 'Construir',
    title: 'Entregamos valor por etapas',
    copy: 'Desarrollamos, probamos e integramos con avances claros y utilizables.',
  },
  {
    label: 'Evolucionar',
    title: 'Medimos y mejoramos',
    copy: 'Acompañamos la adopción y hacemos que la solución crezca con la operación.',
  },
];

const partnerPoints = [
  'Objetivos y alcance visibles desde el inicio.',
  'Integración con sus procesos y sistemas actuales.',
  'Seguridad, calidad y escalabilidad desde el diseño.',
  'Entregas progresivas que aceleran el aprendizaje.',
];

function PagerIcon({ direction }) {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true' focusable='false'>
      <path
        d={direction === 'prev' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'}
        fill='none'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function useScrollPager(trackRef) {
  const [edges, setEdges] = useState({ atStart: true, atEnd: false });

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setEdges({
      atStart: track.scrollLeft <= 4,
      atEnd: maxScroll <= 4 || track.scrollLeft >= maxScroll - 4,
    });
  }, [trackRef]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    update();
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      track.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [trackRef, update]);

  const page = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('li');
    const gap = parseFloat(window.getComputedStyle(track).columnGap) || 20;
    const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (typeof track.scrollBy === 'function') {
      track.scrollBy({ left: direction * step, behavior: reduce ? 'auto' : 'smooth' });
    }
  };

  return { ...edges, page };
}

function HomePage() {
  const trackRef = useRef(null);
  const { atStart, atEnd, page } = useScrollPager(trackRef);

  return (
    <>
      <SEO
        title='Vekian Labs | Datos, Software, Cloud e Innovación'
        description='Diseñamos soluciones de datos, business intelligence, software a medida, cloud y experiencias digitales que ayudan a las empresas a operar mejor y crecer.'
        canonical='https://www.vekianlabs.com/'
        keywords='Vekian Labs, análisis de datos, business intelligence, software a medida, cloud, desarrollo frontend, ingeniería de software, innovación, Ecuador, Latinoamérica'
      />

      <main id='main' tabIndex={-1} className='home'>
        {/* 1 — Stage */}
        <section className='home-stage' aria-labelledby='home-title'>
          <div className='vk-container home-stage-copy'>
            <p className='vk-kicker home-stage-kicker'>Vekian Labs</p>
            <h1 id='home-title' className='vk-display home-stage-title'>
              Convertimos datos y software en una ventaja para su negocio.
            </h1>
            <p className='vk-lead home-stage-lead'>
              Diseñamos, construimos y escalamos soluciones digitales que simplifican la operación,
              mejoran las decisiones y crean nuevas oportunidades.
            </p>
            <div className='vk-actions home-stage-actions'>
              <Link to='/contacto' className='vk-btn vk-btn--primary vk-btn--lg'>
                Cuéntenos su reto
              </Link>
              <Link to='/servicios' className='vk-link'>Ver servicios</Link>
            </div>
          </div>

          <div className='vk-container vk-container--wide home-stage-media-wrap'>
            <figure className='vk-media home-stage-media'>
              <img
                src='/images/home-carousel-ingenieria.webp'
                alt='Equipo de trabajo colaborando alrededor de una mesa con portátiles y tabletas'
                width='1600'
                height='1068'
                fetchpriority='high'
                decoding='async'
              />
            </figure>

            <div className='home-capsule'>
              <p className='home-capsule-text'>
                <strong>Una conversación de 30 minutos</strong>
                <span>para entender su reto y el camino más directo.</span>
              </p>
              <Link to='/contacto' className='vk-btn vk-btn--dark vk-btn--sm home-capsule-cta'>
                Agendar
              </Link>
            </div>
          </div>

          <div className='vk-container'>
            <ul className='home-proof' aria-label='Vekian Labs en cifras'>
              {proofPoints.map((point) => (
                <li className='home-proof-item' key={point.label}>
                  <span className='home-proof-value'>{point.value}</span>
                  <span className='home-proof-label'>{point.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 2 — Highlights */}
        <section className='vk-section vk-section--mist home-highlights' aria-labelledby='solutions-title'>
          <div className='vk-container'>
            <div className='vk-section-header home-highlights-header'>
              <div>
                <p className='vk-eyebrow'>Lo que hacemos</p>
                <h2 id='solutions-title' className='vk-headline'>
                  Todo lo necesario para transformar una idea en resultados.
                </h2>
              </div>
              <Link to='/servicios' className='vk-link home-highlights-all'>Ver todos los servicios</Link>
            </div>
          </div>

          <ul
            className='home-track'
            ref={trackRef}
            id='home-track'
            aria-label='Capacidades de Vekian Labs'
            tabIndex={0}
          >
            {capabilities.map((capability) => (
              <li className='home-card' key={capability.title}>
                <div className='home-card-body'>
                  <p className='home-card-tags'>{capability.tags.join(' · ')}</p>
                  <h3 className='vk-card-title home-card-title'>{capability.title}</h3>
                  <p className='home-card-copy'>{capability.copy}</p>
                  <Link to={capability.link} className='vk-link home-card-link'>
                    Conocer la solución<span className='vk-sr-only'> de {capability.title}</span>
                  </Link>
                </div>
                <div className='home-card-media' aria-hidden='true'>
                  <img
                    src={capability.image}
                    alt=''
                    width='800'
                    height='560'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </li>
            ))}
          </ul>

          <div className='vk-container home-pager-row'>
            <div className='home-pager' role='group' aria-label='Desplazar capacidades'>
              <button
                type='button'
                className='home-pager-btn'
                onClick={() => page(-1)}
                disabled={atStart}
                aria-controls='home-track'
                aria-label='Capacidad anterior'
              >
                <PagerIcon direction='prev' />
              </button>
              <button
                type='button'
                className='home-pager-btn'
                onClick={() => page(1)}
                disabled={atEnd}
                aria-controls='home-track'
                aria-label='Capacidad siguiente'
              >
                <PagerIcon direction='next' />
              </button>
            </div>
          </div>
        </section>

        {/* 3 — Resultados (bloques editoriales) */}
        <section className='vk-section home-outcomes' aria-labelledby='outcomes-title'>
          <div className='vk-container'>
            <div className='home-outcomes-intro'>
              <div>
                <p className='vk-eyebrow'>El valor se nota en la operación</p>
                <h2 id='outcomes-title' className='vk-headline'>Tecnología útil desde el primer vistazo.</h2>
                <p className='vk-lead'>
                  Cada solución se conecta con un resultado de negocio concreto. Menos ruido,
                  más control y una base sólida para crecer.
                </p>
                <Link to='/proyectos' className='vk-link'>Ver proyectos</Link>
              </div>
              <nav className='home-outcomes-index' aria-label='Resultados que buscamos'>
                <p className='vk-label'>Cuatro resultados que buscamos</p>
                <ol className='vk-index'>
                  {businessOutcomes.map((outcome) => (
                    <li key={outcome.number}>
                      <a href={`#resultado-${outcome.number}`}>
                        <span className='vk-index__number'>{outcome.number}</span>
                        <span>
                          <span className='vk-index__title'>{outcome.title}</span>
                          <span className='vk-index__meta'>{outcome.service.name}</span>
                        </span>
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </div>

            <div className='home-editorials'>
              {businessOutcomes.map((outcome, index) => (
                <Reveal
                  as='article'
                  className={`home-editorial ${index % 2 ? 'is-reversed' : ''}`}
                  id={`resultado-${outcome.number}`}
                  key={outcome.number}
                >
                  <div className='home-editorial-copy'>
                    <p className='home-editorial-number'>{outcome.number}</p>
                    <h3 className='home-editorial-title'>{outcome.title}.</h3>
                    <p className='home-editorial-text'>{outcome.copy}</p>
                    <div className='home-editorial-practice'>
                      <p className='vk-label'>En la práctica</p>
                      <ul className='vk-practice'>
                        {outcome.practice.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <Link to={outcome.service.path} className='vk-link home-editorial-link'>
                      Servicio relacionado: {outcome.service.name}
                    </Link>
                  </div>
                  <figure className='vk-media home-editorial-media'>
                    <img
                      src={outcome.image}
                      alt={outcome.alt}
                      width='1200'
                      height='900'
                      loading='lazy'
                      decoding='async'
                    />
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* 4 — Proceso */}
        <section className='vk-section vk-section--mist home-process' aria-labelledby='delivery-title'>
          <div className='vk-container'>
            <div className='home-process-header'>
              <p className='vk-eyebrow'>Cómo trabajamos</p>
              <h2 id='delivery-title' className='vk-headline'>Un proceso claro. Sin cajas negras.</h2>
              <p className='vk-lead'>
                Trabajamos por etapas visibles para reducir riesgo, validar temprano y mantener
                cada decisión alineada con el negocio.
              </p>
            </div>

            <ol className='home-steps'>
              {deliverySteps.map((step, index) => (
                <li className='home-step' key={step.label}>
                  <span className='home-step-number' aria-hidden='true'>{index + 1}</span>
                  <p className='home-step-label'>{step.label}</p>
                  <h3 className='home-step-title'>{step.title}</h3>
                  <p className='home-step-copy'>{step.copy}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 5 — Industrias */}
        <section className='vk-section home-industries' aria-labelledby='industries-title'>
          <div className='vk-container home-industries-grid'>
            <div className='home-industries-copy'>
              <p className='vk-eyebrow'>Experiencia por industria</p>
              <h2 id='industries-title' className='vk-headline'>
                La misma tecnología no resuelve todos los negocios.
              </h2>
              <p className='home-industries-text'>
                Adaptamos cada solución a los procesos, riesgos y oportunidades de su sector.
                Así reducimos la curva de aprendizaje y aceleramos el impacto.
              </p>
              <Link to='/industrias' className='vk-link'>Ver todas las industrias</Link>
            </div>

            <ul className='home-industry-list'>
              {industries.map(([name, path]) => (
                <li key={path}>
                  <Link to={path} className='home-industry-link'>{name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 6 — Socios (única banda graphite) */}
        <section className='vk-section vk-section--graphite home-partner' aria-labelledby='partner-title'>
          <div className='vk-container home-partner-grid'>
            <div className='home-partner-copy'>
              <p className='home-partner-eyebrow'>Por qué Vekian Labs</p>
              <h2 id='partner-title' className='vk-headline'>
                Pensamos como socios, construimos como especialistas.
              </h2>
              <p className='vk-lead'>
                No entregamos tecnología aislada. Integramos estrategia, experiencia,
                ingeniería y acompañamiento para que la solución funcione en el mundo real.
              </p>
              <Link to='/nosotros' className='vk-link'>Conocer Vekian Labs</Link>
            </div>

            <ul className='home-partner-list'>
              {partnerPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* 7 — Cierre */}
        <section className='vk-section home-final' aria-labelledby='home-cta-title'>
          <div className='vk-container vk-container--text home-final-inner'>
            <p className='vk-kicker'>Su próximo avance puede empezar aquí</p>
            <h2 id='home-cta-title' className='vk-display home-final-title'>¿Qué necesita mejorar hoy?</h2>
            <p className='vk-lead home-final-lead'>
              Conversemos sobre el reto, el resultado esperado y la forma más directa de llegar.
            </p>
            <div className='vk-actions home-final-actions'>
              <Link to='/contacto' className='vk-btn vk-btn--primary vk-btn--lg'>
                Agendar una conversación
              </Link>
              <a href={CONTACT_MAILTO} className='vk-link'>{CONTACT_EMAIL}</a>
            </div>
            <ContactChannels className='home-final-channels' showEmail={false} label='' />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default HomePage;
